import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import ThriveModal from "../../common/ThriveModal";
import { axiosInstance } from "../../../utils/networking";
import {
  OutcomeRecordKind,
  OutcomeRecordType,
  defaultOutcome,
} from "../../../interfaces";

export const outcomeTypeLabels: { [key in OutcomeRecordKind]: string } = {
  business_started: "Business Started",
  business_expanded: "Business Expanded",
  jobs_created: "Jobs Created",
  jobs_retained: "Jobs Retained",
  revenue_growth: "Revenue Growth",
  capital_accessed: "Capital Accessed",
  loan: "Loan",
  grant: "Grant",
  certification: "Certification",
};

export const OUTCOME_TYPE_OPTIONS = Object.keys(
  outcomeTypeLabels,
) as OutcomeRecordKind[];

export const DOLLAR_OUTCOME_TYPES: OutcomeRecordKind[] = [
  "revenue_growth",
  "capital_accessed",
  "loan",
  "grant",
];
export const COUNT_OUTCOME_TYPES: OutcomeRecordKind[] = [
  "jobs_created",
  "jobs_retained",
];

export const outcomeHasValue = (type: OutcomeRecordKind) =>
  DOLLAR_OUTCOME_TYPES.includes(type) || COUNT_OUTCOME_TYPES.includes(type);

export const formatOutcomeValue = (outcome: OutcomeRecordType): string => {
  if (outcome.value === null || outcome.value === undefined) return "";
  if (DOLLAR_OUTCOME_TYPES.includes(outcome.type)) {
    return `$${Number(outcome.value).toLocaleString()}`;
  }
  if (COUNT_OUTCOME_TYPES.includes(outcome.type)) {
    return `${Number(outcome.value).toLocaleString()} jobs`;
  }
  return "";
};

type OutcomeEditModalProps = {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  caseId: string;
  firmId: string;
  initial?: OutcomeRecordType;
};

// Create/edit a single economic-impact outcome. POSTs /outcomes (insert when
// _id is empty, update otherwise) — value semantics per type are mirrored on
// the server (outcomeModel.coerceValue).
const OutcomeEditModal: React.FC<OutcomeEditModalProps> = ({
  open,
  onClose,
  onSaved,
  caseId,
  firmId,
  initial,
}) => {
  const [outcome, setOutcome] = useState<OutcomeRecordType>({
    ...defaultOutcome,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOutcome(
      initial
        ? { ...initial }
        : { ...defaultOutcome, case_id: caseId, firm_id: firmId },
    );
  }, [open, initial, caseId, firmId]);

  const showValue = outcomeHasValue(outcome.type);
  const isDollar = DOLLAR_OUTCOME_TYPES.includes(outcome.type);

  const handleSave = () => {
    setSaving(true);
    axiosInstance
      .post(`/outcomes`, {
        ...outcome,
        case_id: outcome.case_id || caseId,
        firm_id: outcome.firm_id || firmId,
        value: showValue ? outcome.value : null,
      })
      .then(() => {
        setSaving(false);
        onSaved();
        onClose();
      })
      .catch(() => setSaving(false));
  };

  const setOpenProxy = (value: boolean) => {
    if (!value) onClose();
  };

  return (
    <ThriveModal
      open={open}
      setOpen={setOpenProxy}
      closeByClick={true}
      width={520}
      height={480}
      contentComponent={
        <Box sx={{ padding: "24px" }}>
          <Typography variant="h6" sx={{ marginBottom: "16px" }}>
            {outcome._id ? "Edit Outcome" : "Record Outcome"}
          </Typography>
          <Stack spacing={2}>
            <Select
              size="small"
              value={outcome.type}
              onChange={(e) =>
                setOutcome({
                  ...outcome,
                  type: e.target.value as OutcomeRecordKind,
                })
              }
            >
              {OUTCOME_TYPE_OPTIONS.map((type) => (
                <MenuItem key={type} value={type}>
                  {outcomeTypeLabels[type]}
                </MenuItem>
              ))}
            </Select>
            {showValue && (
              <TextField
                size="small"
                type="number"
                label={isDollar ? "Amount" : "Number of jobs"}
                value={outcome.value ?? ""}
                onChange={(e) =>
                  setOutcome({
                    ...outcome,
                    value: e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                InputProps={{
                  startAdornment: isDollar ? (
                    <InputAdornment position="start">$</InputAdornment>
                  ) : undefined,
                  endAdornment: !isDollar ? (
                    <InputAdornment position="end">jobs</InputAdornment>
                  ) : undefined,
                }}
              />
            )}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date"
                value={dayjs(outcome.date)}
                onChange={(value: any) =>
                  setOutcome({
                    ...outcome,
                    date: value ? value.toDate() : new Date(),
                  })
                }
                slotProps={{ textField: { size: "small" } }}
              />
            </LocalizationProvider>
            <TextField
              size="small"
              label="Note (grant name, certification, narrative…)"
              multiline
              rows={3}
              value={outcome.note}
              onChange={(e) => setOutcome({ ...outcome, note: e.target.value })}
            />
            <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
              <Button onClick={onClose}>Cancel</Button>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                Save
              </Button>
            </Stack>
          </Stack>
        </Box>
      }
    />
  );
};

export default OutcomeEditModal;
